import React from 'react';
import { Typography } from '@mui/material';
import LegalPageLayout from './LegalPageLayout';

const RefundPolicy = () => {
  return (
    <LegalPageLayout
      title="Refund Policy"
      updated="October 13, 2026"
    >
      <Typography paragraph>
        This Refund Policy applies to relevant payments associated
        with HaggleFreight, a transportation marketplace operated
        by GoCourier Inc. It supplements the Terms of Service,
        Cancellation Policy, and applicable accepted purchase or
        transportation agreement.
      </Typography>

      <Typography paragraph>
        Platform charges, transportation charges owed to a
        Transportation Provider, and third-party processing charges
        are different categories. This policy does not create a
        charge, authorize an unknown future payment, or guarantee
        reimbursement for every shipment issue.
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Completed Services
      </Typography>

      <Typography paragraph>
        A transportation service is completed when the agreed
        transportation obligations have been fulfilled, subject
        to applicable law and the parties' agreement. Pickup alone,
        a bid, payment capture, or a status marked "completed" does
        not by itself establish fulfillment of the entire
        transportation agreement.
      </Typography>

      <Typography paragraph>
        For a separately purchased Platform service or subscription,
        fulfillment depends on the service promised in the accepted
        offer. A system record is relevant evidence, not conclusive
        proof that a disputed service was properly performed.
      </Typography>

      <Typography paragraph>
        A change-of-mind refund for a properly performed service
        may be unavailable under the accepted terms. This does not
        excuse nonperformance, a material service failure, an
        incorrect charge, or another ground for relief under
        applicable law.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Failed or Incomplete Services
      </Typography>

      <Typography paragraph>
        Report duplicate charges, incorrect amounts, unauthorized
        charges, technical payment errors, or services that were
        not provided. We will investigate matters within our
        responsibility and correct confirmed errors through the
        appropriate payment channel.
      </Typography>

      <Typography paragraph>
        For partially performed transportation, the amount properly
        due depends on what was agreed, what was actually performed,
        why performance stopped, and applicable law. A provider is
        not automatically entitled to the entire transportation
        price merely because cargo was picked up.
      </Typography>

      <Typography paragraph>
        A pending authorization is different from a captured
        payment. Releasing an authorization and refunding a
        completed charge may appear differently on a statement.
        Contact support when the payment status is unclear.
      </Typography>

      <Typography variant="h5" gutterBottom>
        3. Carrier Disputes
      </Typography>

      <Typography paragraph>
        A dispute about transportation performance may require
        review of the accepted terms, shipment documents, messages,
        photographs, delivery records, payment records, and other
        relevant evidence. We may request reasonably necessary
        information and contact the transaction participants.
      </Typography>

      <Typography paragraph>
        HaggleFreight may assist with communication and actions
        within its responsibility. Support review is not binding
        arbitration and does not remove a right to use a carrier
        claim process, insurer, payment provider, regulator, or
        court.
      </Typography>

      <Typography paragraph>
        Submitting a request does not guarantee a particular
        outcome. We will explain decisions within our responsibility
        and consider relevant additional information. Do not seek
        duplicate recovery for the same loss.
      </Typography>

      <Typography variant="h5" gutterBottom>
        4. Additional Charges and Non-Refundable Amounts
      </Typography>

      <Typography paragraph>
        An amount may be retained or deducted only when supported
        by an applicable accepted term or another lawful basis.
        Where advance disclosure and agreement are required, a
        general reference to possible fees is not sufficient to
        authorize an arbitrary deduction.
      </Typography>

      <Typography paragraph>
        Waiting time, return transportation, storage, redelivery,
        or other additional services may be payable under the
        parties' accepted agreement or applicable law. Identify
        who is entitled to the amount and how it was calculated.
      </Typography>

      <Typography paragraph>
        A payment provider declining to return its fee to
        GoCourier Inc. does not automatically make that fee
        chargeable to the user. A deduction must be permitted by
        applicable terms and law and cannot reduce a refund that
        must legally be provided in full.
      </Typography>

      <Typography variant="h5" gutterBottom>
        5. Cancellations Before Carrier Acceptance
      </Typography>

      <Typography paragraph>
        A Customer may withdraw an unaccepted shipment request
        without a shipment-cancellation fee. A Transportation
        Provider viewing a listing, bidding, or discussing pricing
        does not itself create a confirmed booking or charge.
      </Typography>

      <Typography paragraph>
        If transportation funds were collected for a shipment
        that was never confirmed or performed, their disposition
        must follow the payment authorization, applicable law,
        and the Cancellation Policy. A separately purchased
        subscription or service remains governed by its accepted
        purchase terms.
      </Typography>

      <Typography variant="h5" gutterBottom>
        6. Cancellations After Carrier Acceptance
      </Typography>

      <Typography paragraph>
        Following mutual acceptance, an agreed cancellation charge
        or payment for an actually performed portion of service
        may be due. The relevant amount or calculation method must
        have been disclosed and accepted where required.
      </Typography>

      <Typography paragraph>
        The remaining payment, if any, must be handled under the
        applicable agreement and law. This policy does not establish
        a default cancellation percentage or authorize keeping
        the entire payment solely because the shipment was
        accepted.
      </Typography>

      <Typography variant="h5" gutterBottom>
        7. Cancellations After Pickup
      </Typography>

      <Typography paragraph>
        After pickup, stopping, returning, or redirecting cargo
        requires appropriate communication and compliance with
        lawful custody and transportation obligations. The
        Cancellation Policy addresses that process.
      </Typography>

      <Typography paragraph>
        Financial consequences depend on services actually
        performed, accepted terms, and applicable law. Pickup
        does not automatically eliminate all refund rights.
        Return, storage, redelivery, or other additional charges
        require an applicable contractual or legal basis.
      </Typography>

      <Typography variant="h5" gutterBottom>
        8. Damaged, Lost, or Delayed Shipments
      </Typography>

      <Typography paragraph>
        Promptly notify the Transportation Provider and relevant
        insurer of cargo loss, damage, delay, or other issues.
        Contact HaggleFreight for available Platform support and
        preserve relevant documents and evidence.
      </Typography>

      <Typography paragraph>
        A refund of transportation charges and compensation for
        cargo loss or damage are different issues. Resolving one
        does not automatically waive the other. GoCourier Inc.
        does not itself provide cargo insurance or guarantee
        payment of every cargo claim.
      </Typography>

      <Typography paragraph>
        These limitations do not exclude responsibilities imposed
        on us by law or an express commitment we made. A support
        request does not extend a statutory, contractual,
        insurance, or payment-provider deadline.
      </Typography>

      <Typography variant="h5" gutterBottom>
        9. Subscriptions and Free Access
      </Typography>

      <Typography paragraph>
        Access provided without a Platform charge does not create
        a refundable Platform payment. No fixed free-access
        duration, future subscription price, or billing start
        date is established by this policy.
      </Typography>

      <Typography paragraph>
        When subscriptions are offered, the applicable purchase
        screen must explain billing, cancellation, and refund
        terms before purchase. This policy does not make every
        future subscription automatically non-refundable.
      </Typography>

      <Typography paragraph>
        Cancelling renewal does not necessarily refund a correctly
        billed prior period. Refund rights depend on the accepted
        offer, service provided, purchase channel, and applicable
        mandatory protections. Apple or Google purchases are
        handled through the relevant store process where
        applicable.
      </Typography>

      <Typography variant="h5" gutterBottom>
        10. Chargebacks and Payment Disputes
      </Typography>

      <Typography paragraph>
        You may contact support before disputing a payment, but
        doing so is not a mandatory prerequisite to exercising
        a bank, card, app-store, or legal right.
      </Typography>

      <Typography paragraph>
        A good-faith chargeback, complaint, or refund request is
        not itself misconduct. False statements, fabricated
        evidence, stolen credentials, or attempts to obtain
        duplicate recovery may be investigated.
      </Typography>

      <Typography paragraph>
        Any account or payment restriction must have a legitimate,
        proportionate basis and comply with applicable terms,
        provider requirements, and law. A dispute does not
        authorize confiscation of unrelated funds.
      </Typography>

      <Typography variant="h5" gutterBottom>
        11. Refund Method and Processing Time
      </Typography>

      <Typography paragraph>
        Approved refunds ordinarily use the original payment
        channel. An alternative may be used when necessary and
        lawful with appropriate safeguards. We will not substitute
        credit for a monetary refund without agreement or another
        lawful basis.
      </Typography>

      <Typography paragraph>
        We will communicate the status of an approved refund and
        process actions within our responsibility without
        unreasonable delay. Posting time depends on the payment
        provider, bank, or app store. We do not guarantee a
        bank-arrival date outside our control.
      </Typography>

      <Typography variant="h5" gutterBottom>
        12. Contact
      </Typography>

      <Typography paragraph>
        Send requests to support@hagglefreight.com with your account
        email, shipment or transaction identifier, disputed amount,
        and explanation. Do not send passwords, complete card
        numbers, card security codes, or bank-login credentials.
      </Typography>

      <Typography paragraph>
        Mandatory rights take priority. Changes are handled under
        the Terms of Service and do not retroactively remove rights
        from an earlier transaction.
      </Typography>
    </LegalPageLayout>
  );
};

export default RefundPolicy;